import { format, fromUnixTime } from 'date-fns';
import { DigitalOrdersBase } from '../../models/orders';
import { NewOrExisting } from '../../models/organizations';
import { findFacilityKey } from '../../libs/helpers/facilityKeys';

/**
 * Resolvers that turn stored IDs into display names, mirroring how OrdersPanel resolves
 * existing organizations/facilities from the MobX store maps.
 */
export interface OrderExportResolvers {
  getOrgName: (organizationId: number | string | undefined) => string | undefined;
  getFacilityName: (facilityId: number | string | undefined) => string | undefined;
}

// `productDetails` is declared `Record<string, string>` on the order model, but at runtime
// holds nested objects for products such as AM_VISION. Narrow to the shape we read here.
interface AmVisionFacilityCounts {
  amVisionTouchscreenCount?: string | number;
  amVisionLEDCount?: string | number;
}

interface ExportProductDetails {
  smallBusinessUnit?: string;
  customerContactName?: string;
  customerContactEmail?: string;
  AM_VISION?: Record<string, AmVisionFacilityCounts>;
}

interface ExportRow {
  fulfilledDate: string;
  sbu: string;
  organization: string;
  facility: string;
  contactName: string;
  contactEmail: string;
  assetManagementQty: string;
}

// Single source of truth for column order — header and rows are both derived from this.
const COLUMNS: ReadonlyArray<{ header: string; key: keyof ExportRow }> = [
  { header: 'Fulfilled Date', key: 'fulfilledDate' },
  { header: 'Strategic Business Unit', key: 'sbu' },
  { header: 'Organization (ID)', key: 'organization' },
  { header: 'Facility Name (ID)', key: 'facility' },
  { header: 'Customer Contact Name', key: 'contactName' },
  { header: 'Customer Contact Email', key: 'contactEmail' },
  { header: 'Quantity of Asset Management', key: 'assetManagementQty' },
];

const escapeCsvField = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const withId = (name: string | undefined, id: number | string | undefined): string => {
  if (id === undefined || id === null || id === '') {
    return name ?? '';
  }
  return `${name ?? ''} (${id})`.trim();
};

const resolveOrgLabel = (order: DigitalOrdersBase, resolvers: OrderExportResolvers): string => {
  const org = order.organization;
  const isNew = org.newOrExistingOrganization === NewOrExisting.NEW;
  const name = isNew && 'name' in org ? org.name : resolvers.getOrgName(org.organizationId);
  return withId(name, org.organizationId);
};

// AM_VISION is keyed by facility name. Orders saved before DO-295 had those keys mangled by
// humps on the API round-trip, so match on the normalized key — see libs/helpers/facilityKeys.ts.
const amVisionQuantity = (details: ExportProductDetails, facilityName: string): number => {
  const amVision = details.AM_VISION;
  if (!amVision || !facilityName) {
    return 0;
  }
  const matchKey = findFacilityKey(Object.keys(amVision), facilityName);
  const counts = matchKey ? amVision[matchKey] : undefined;
  return (Number(counts?.amVisionTouchscreenCount) || 0) + (Number(counts?.amVisionLEDCount) || 0);
};

/**
 * Builds one CSV row per facility across the given (fulfilled) orders. Order-level fields
 * repeat for each of the order's facilities. Returns the full CSV as a string (no BOM —
 * the BOM is added at download time so the string stays clean for tests/reuse).
 */
export const buildFulfilledOrdersCsv = (
  orders: DigitalOrdersBase[],
  resolvers: OrderExportResolvers,
): string => {
  const rows: ExportRow[] = [];

  orders.forEach(order => {
    const details = ((order.productDetails ?? {}) as unknown) as ExportProductDetails;
    const fulfilledDate = order.fulfillmentTime
      ? format(fromUnixTime(order.fulfillmentTime), 'MM-dd-yyyy')
      : 'N/A';
    const orgLabel = resolveOrgLabel(order, resolvers);

    order.facility.facilities.forEach(fac => {
      const facilityName = fac.name || resolvers.getFacilityName(fac.facilityId) || '';
      rows.push({
        fulfilledDate,
        sbu: details.smallBusinessUnit ?? '',
        organization: orgLabel,
        facility: withId(facilityName, fac.facilityId),
        contactName: details.customerContactName ?? '',
        contactEmail: details.customerContactEmail ?? '',
        assetManagementQty: String(amVisionQuantity(details, facilityName)),
      });
    });
  });

  const headerLine = COLUMNS.map(col => escapeCsvField(col.header)).join(',');
  const dataLines = rows.map(row => COLUMNS.map(col => escapeCsvField(row[col.key])).join(','));
  return [headerLine, ...dataLines].join('\r\n');
};

export const fulfilledOrdersCsvFilename = (date: Date = new Date()): string =>
  `fulfilled-digital-orders-${format(date, 'yyyy-MM-dd')}.csv`;

// UTF-8 byte-order mark (U+FEFF). Prepended to the CSV so Excel detects UTF-8 and renders
// accented characters correctly. Built via char code so the source stays free of an
// invisible character.
const UTF8_BOM = String.fromCharCode(0xfeff);

/**
 * Triggers a browser download of the CSV as a UTF-8 file, BOM-prefixed for Excel.
 */
export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([UTF8_BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
